import { useQuery } from '@tanstack/react-query';
import { startTransition, useEffect, useState } from 'react';
import { makeDomainQueryOptions } from '#api/domains.queries.ts';
import { trackError } from '#api/errors.ts';
import { useEditAdminOIDCProvider } from '#api/oidc-providers.queries.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { OIDCProvider } from '#dn-types/oidc-provider.ts';
import { AdminOIDCProviderCard } from '../detail/admin/AdminOIDCProviderCard.tsx';
import { EnablePromptBanner } from './EnablePromptBanner.tsx';
import { useAdminEnableBannerProviderId } from './useShowEnableBanner.ts';

type Props = {
  idps: OIDCProvider[];
};

export function AdminOIDCList({ idps }: Props) {
  const [globalErrorMsg, setGlobalErrorMsg] = useState<string | null>(null);
  const [enableBannerProviderId, , clearEnableBannerProviderId] = useAdminEnableBannerProviderId();
  const providerToPromptEnable = idps.find((p) => p.id === enableBannerProviderId && !p.enabled);
  const { data: { data: domain } = {} } = useQuery({
    ...makeDomainQueryOptions(providerToPromptEnable?.domainID ?? ''),
    enabled: Boolean(providerToPromptEnable?.domainID),
  });
  const editOIDCProviderMutation = useEditAdminOIDCProvider();

  // Clear the enable banner once the provider is enabled or deleted
  useEffect(() => {
    if (enableBannerProviderId && !providerToPromptEnable) {
      clearEnableBannerProviderId();
    }
  }, [enableBannerProviderId, providerToPromptEnable, clearEnableBannerProviderId]);

  async function enablePromptedProvider(provider: OIDCProvider) {
    try {
      await editOIDCProviderMutation.mutateAsync({
        id: provider.id,
        values: { ...provider, clientSecret: null, enabled: true },
      });
    } catch (err) {
      trackError(err);
      setGlobalErrorMsg('Could not enable provider, please refresh and try again');
    }
  }

  return (
    <>
      {globalErrorMsg ?
        <ErrorMessage className="mb-16">{globalErrorMsg}</ErrorMessage>
      : null}
      {providerToPromptEnable && domain ?
        <EnablePromptBanner
          className="mb-40"
          providerName="Admin identity provider"
          domain={domain}
          isEnabling={editOIDCProviderMutation.isPending}
          onEnable={() => {
            startTransition(() => enablePromptedProvider(providerToPromptEnable));
          }}
          onDismiss={clearEnableBannerProviderId}
        />
      : null}
      <h2 className="h4">Providers</h2>
      <Spacer direction="vertical">
        {idps.map((provider) => {
          return (
            <AdminOIDCProviderCard key={provider.id} oidcProvider={provider} setGlobalErrorMsg={setGlobalErrorMsg} />
          );
        })}
      </Spacer>
    </>
  );
}
